import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "@/utils/utils";

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
	{
		variants: {
			variant: {
				default:
					"bg-primary text-primary-content shadow-xs hover:bg-primary/90",
				destructive:
					"bg-error text-error-content shadow-xs hover:bg-error/90 focus-visible:ring-error/20",
				outline:
					"border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
				secondary:
					"bg-secondary text-secondary-content shadow-xs hover:bg-secondary/80",
				/**
				 * Harvest gold. The design system's "second commitment" slot: an
				 * action that is real and irreversible but is not the page's default
				 * next step (Sell, next to the green "New record"). Deliberately not
				 * `secondary` — that token is the lighter gold used for surfaces.
				 */
				accent: "bg-accent text-accent-content shadow-xs hover:bg-accent/90",
				ghost:
					"hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
				link: "text-primary underline-offset-4 hover:underline",
			},
			size: {
				default: "h-9 px-4 py-2 has-[>svg]:px-3",
				sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
				lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
				/**
				 * 44px — the WCAG 2.5.5 AAA / Apple HIG target floor. For controls a
				 * user reaches for with a thumb on a phone, outdoors, at arm's length:
				 * the review surfaces, and anything else whose primary device is a
				 * hand rather than a pointer. `default` (36px) is a pointer size.
				 */
				touch: "h-11 rounded-md px-4 has-[>svg]:px-3",
				icon: "size-9",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

function Button({
	className,
	variant,
	size,
	asChild = false,
	...props
}: React.ComponentProps<"button"> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean;
	}) {
	const Comp = asChild ? Slot : "button";

	return (
		<Comp
			data-slot="button"
			className={cn(buttonVariants({ variant, size, className }))}
			{...props}
		/>
	);
}

export { Button };
